import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import {
  hashKey,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query'
import type { StoreApi } from 'zustand'
import { NAVIGATION_QUERY_STALE_TIME_MS } from '@/constants'
import { FIELD_META_TABS } from '@features/columns/logic/fieldMetaTabs'
import { listAutomations } from '@features/automations/api/automations.api'
import { automationsQueryKeys } from '@features/automations/api/queryKeys'
import type { AutomationWorkspaceId } from '@features/automations/stores/useAutomationViewStore'
import { getAllUserGroups } from '@features/permissions/api/permissions.api'
import { permissionsQueryKeys } from '@features/permissions/api/queryKeys'
import type { IntegrationWorkspaceId } from '@features/integrations/stores/useIntegrationViewStore'
import { getPreferenceDefaults } from '@features/settings/api/preferenceDefaults.api'
import {
  getPreferenceGroup,
  getPreferenceGroups,
} from '@features/settings/api/preferenceGroups.api'
import { settingsQueryKeys } from '@features/settings/api/queryKeys'
import { getSettings } from '@features/settings/api/settings.api'
import { getUserPreferences } from '@features/settings/api/userPreferences.api'
import { tabSlug } from '@features/settings/pages/preferenceGroupLogic'
import { tablesQueryKeys } from '@features/tables/api/queryKeys'
import { gridViewOptions } from '@features/tables/logic/gridViewPath'
import { getTableSchema, getTables } from '@features/tables/api/tables.api'
import { getTablesInDropdownOrder } from '@features/tables/logic/tableOrdering'
import { viewsQueryKeys } from '@features/views/api/queryKeys'
import { getViews } from '@features/views/api/views.api'
import type { RouteParams } from './routeCatalog'

/**
 * Where each route parameter's values come from, so the route picker can list
 * them by name and check a stored address still leads somewhere. A route whose
 * parameter has no source here stays out of the picker until one is added.
 *
 * Sources are found by the parameter's full pattern first
 * (`/integrations/tables/:tableId`, whose ids are not data tables'), then by the
 * parameter alone (`:tableId`).
 */

export interface RouteParamOption {
  /** The address segment: the id, or key, as it appears in the address. */
  value: string
  label: string
}

export interface RouteParamOptionsState {
  /** Undefined while loading. */
  options: RouteParamOption[] | undefined
  isError: boolean
}

/** How current the values `loadOptions` gives must be. */
export type RouteParamFreshness =
  /**
   * What the app already has, at once, refreshed behind it when older than
   * the app keeps its navigation lists (a change shows in the query cache when
   * it lands); fetched only when there's none.
   */
  | 'cached'
  /** Fetched again when older than a minute. */
  | 'recent'
  /** Fetched again now: before acting on a value being missing. */
  | 'fresh'

export interface RouteParamSource {
  /** The field's name, such as "Table". */
  label: string
  /** The values to pick from, given the parameters before this one. A hook. */
  useOptions: (params: RouteParams) => RouteParamOptionsState
  /** The same values outside React ('recent' unless told); null when they can't be had. */
  loadOptions: (
    params: RouteParams,
    queryClient: QueryClient,
    freshness?: RouteParamFreshness
  ) => Promise<RouteParamOption[] | null>
  /**
   * Calls `onChange` when the values `loadOptions` gives for these parameters
   * may have changed (their query's data or their store's list, nothing else),
   * until the returned function is called. What's made from the values (a
   * favorite's name) is worked out again then, and only then.
   */
  watchOptions: (params: RouteParams, queryClient: QueryClient, onChange: () => void) => () => void
  /**
   * A choice that leaves this parameter off, so the page above picks for it
   * (a table opens its default view). Its name, such as "Default" in a View field;
   * the picker shows it in italics, as the stand-in it is.
   */
  fallbackLabel?: string
}

const OPTIONS_STALE_TIME_MS = 60_000

const watchNothing = () => () => undefined

/** Every table's All Records, in a view's place in its address (`/table-views/all`). */
const ALL_RECORDS_VIEW_ID = 'all'

interface QuerySpec<TData> {
  queryKey: QueryKey
  queryFn: () => Promise<TData>
}

/** A source backed by a query the app already makes. */
function querySource<TData>(
  label: string,
  query: (params: RouteParams) => QuerySpec<TData> | null,
  toOptions: (data: TData, params: RouteParams) => RouteParamOption[],
  extra: Pick<RouteParamSource, 'fallbackLabel'> = {}
): RouteParamSource {
  return {
    label,
    ...extra,
    useOptions(params) {
      const spec = query(params)
      const result = useQuery<TData>({
        queryKey: spec?.queryKey ?? ['route-picker', 'unavailable', label],
        queryFn: spec?.queryFn ?? (() => Promise.reject(new Error('Unavailable'))),
        enabled: spec !== null,
        staleTime: OPTIONS_STALE_TIME_MS,
      })
      return {
        options: spec && result.data !== undefined ? toOptions(result.data, params) : undefined,
        isError: result.isError,
      }
    },
    async loadOptions(params, queryClient, freshness = 'recent') {
      const spec = query(params)
      if (!spec) return null
      try {
        const cached =
          freshness === 'cached' ? queryClient.getQueryData<TData>(spec.queryKey) : undefined
        if (cached !== undefined) {
          // No more often than the app refreshes the list itself: favorites'
          // and recent pages' names are read from it again and again.
          void queryClient.prefetchQuery({ ...spec, staleTime: NAVIGATION_QUERY_STALE_TIME_MS })
          return toOptions(cached, params)
        }
        const data = await queryClient.fetchQuery({
          ...spec,
          staleTime: freshness === 'fresh' ? 0 : OPTIONS_STALE_TIME_MS,
        })
        return toOptions(data, params)
      } catch {
        return null
      }
    },
    watchOptions(params, queryClient, onChange) {
      const spec = query(params)
      if (!spec) return watchNothing()
      const queryHash = hashKey(spec.queryKey)
      // Refetches that bring the same data keep it (structural sharing): no change.
      let data = queryClient.getQueryData(spec.queryKey)
      return queryClient.getQueryCache().subscribe((event) => {
        if (event.type !== 'updated' || event.query.queryHash !== queryHash) return
        if (event.query.state.data === data) return
        data = event.query.state.data
        onChange()
      })
    },
  }
}

/** A store the app keeps in the browser, and how a source reads its options. */
interface StoreBinding<TState, TItem> {
  store: StoreApi<TState>
  /** The store's own list (a stable reference), which options are made from. */
  selectList: (state: TState) => TItem[]
  toOptions: (items: TItem[]) => RouteParamOption[]
  /**
   * The store holds the server's data, so an item missing from it is gone.
   * Until then its list still names what it has, but a 'fresh' check (made
   * before acting on an item being missing) can't tell, and gets null. Left
   * out, the store is the whole list.
   */
  hasServerData?: (state: TState) => boolean
  /**
   * Reads the server's data into the store, for a store that otherwise gets
   * it only from its own pages: the options wait for it (the picker loads)
   * rather than name what the store has, which may not hold the item at all.
   */
  loadServerData?: (queryClient: QueryClient) => Promise<void>
}

/** A module loaded on first use, then kept, so later readers have it at once. */
function loadOnce<T>(load: () => Promise<T>) {
  let loaded: T | undefined
  let loading: Promise<T> | null = null
  return {
    current: () => loaded,
    load: () => {
      loading ??= load().then(
        (value) => {
          loaded = value
          return value
        },
        (error: unknown) => {
          // A chunk that didn't load (offline) is tried again next time.
          loading = null
          throw error
        }
      )
      return loading
    },
  }
}

const subscribeToNothing = () => () => undefined

/** The store's list waits on the server's data, which its binding can read in. */
function isAwaitingServer<TState, TItem>(binding: StoreBinding<TState, TItem>, state: TState) {
  return Boolean(binding.loadServerData && binding.hasServerData && !binding.hasServerData(state))
}

/**
 * A source backed by a store the app keeps in the browser. The store comes
 * with its feature's code (`loadBinding` imports it), so the app's first
 * load doesn't carry that feature: the options are undefined (loading) until
 * it arrives.
 */
function storeSource<TState, TItem>(
  label: string,
  loadBinding: () => Promise<StoreBinding<TState, TItem>>
): RouteParamSource {
  const binding = loadOnce(loadBinding)
  return {
    label,
    useOptions() {
      const queryClient = useQueryClient()
      const [loaded, setLoaded] = useState(binding.current)
      const [isError, setIsError] = useState(false)
      useEffect(() => {
        if (loaded) return
        let cancelled = false
        binding.load().then(
          (value) => {
            if (!cancelled) setLoaded(value)
          },
          () => {
            if (!cancelled) setIsError(true)
          }
        )
        return () => {
          cancelled = true
        }
      }, [loaded])
      const items = useSyncExternalStore(loaded?.store.subscribe ?? subscribeToNothing, () =>
        loaded ? loaded.selectList(loaded.store.getState()) : undefined
      )
      const awaitingServer = useSyncExternalStore(
        loaded?.store.subscribe ?? subscribeToNothing,
        () => (loaded ? isAwaitingServer(loaded, loaded.store.getState()) : false)
      )
      useEffect(() => {
        if (!loaded?.loadServerData || !awaitingServer) return
        let cancelled = false
        loaded.loadServerData(queryClient).catch(() => {
          if (!cancelled) setIsError(true)
        })
        return () => {
          cancelled = true
        }
      }, [awaitingServer, loaded, queryClient])
      const options = useMemo(
        () => (loaded && items && !awaitingServer ? loaded.toOptions(items) : undefined),
        [awaitingServer, items, loaded]
      )
      return { options, isError }
    },
    async loadOptions(_params, queryClient, freshness = 'recent') {
      try {
        const loaded = await binding.load()
        if (isAwaitingServer(loaded, loaded.store.getState())) {
          await loaded.loadServerData?.(queryClient)
        }
        const { store, selectList, toOptions, hasServerData } = loaded
        const state = store.getState()
        if (freshness === 'fresh' && hasServerData && !hasServerData(state)) return null
        return toOptions(selectList(state))
      } catch {
        return null
      }
    },
    watchOptions(_params, _queryClient, onChange) {
      let unsubscribe: (() => void) | null = null
      let stopped = false
      void binding.load().then(
        ({ store, selectList }) => {
          if (stopped) return
          let list = selectList(store.getState())
          unsubscribe = store.subscribe((state) => {
            const next = selectList(state)
            if (next === list) return
            list = next
            onChange()
          })
        },
        // A store that didn't load has no changes to tell.
        () => undefined
      )
      return () => {
        stopped = true
        unsubscribe?.()
      }
    },
  }
}

/** A source with a fixed list. */
function fixedSource(label: string, options: RouteParamOption[]): RouteParamSource {
  return {
    label,
    useOptions() {
      return { options, isError: false }
    },
    loadOptions() {
      return Promise.resolve(options)
    },
    watchOptions: watchNothing,
  }
}

/**
 * The View field's choice for no view of its own: a grid page's own address
 * opens the view it was last on (see gridViewPath), a table's its default view.
 */
const DEFAULT_VIEW_LABEL = 'Default'

/** The views an Automations grid's address can name, from its view store. */
function automationViewSource(workspaceId: AutomationWorkspaceId): RouteParamSource {
  return {
    ...storeSource('View', async () => {
      const {
        AUTOMATION_DEFAULT_VIEWS,
        automationScopeViews,
        automationViewScope,
        useAutomationViewStore,
      } = await import('@features/automations/stores/useAutomationViewStore')
      const scope = automationViewScope(workspaceId)
      return {
        store: useAutomationViewStore,
        selectList: (state) => state.views,
        toOptions: (views) =>
          gridViewOptions(automationScopeViews(views, scope), AUTOMATION_DEFAULT_VIEWS[scope].name),
      }
    }),
    fallbackLabel: DEFAULT_VIEW_LABEL,
  }
}

/** The views an Integrations grid's address can name, from its view store. */
function integrationViewSource(workspaceId: IntegrationWorkspaceId): RouteParamSource {
  return {
    ...storeSource('View', async () => {
      const {
        INTEGRATION_DEFAULT_VIEWS,
        INTEGRATION_WORKSPACE_SCOPES,
        integrationWorkspaceViews,
        useIntegrationViewStore,
      } = await import('@features/integrations/stores/useIntegrationViewStore')
      const scope = INTEGRATION_WORKSPACE_SCOPES[workspaceId]
      return {
        store: useIntegrationViewStore,
        selectList: (state) => state.views,
        toOptions: (views) =>
          gridViewOptions(
            integrationWorkspaceViews(workspaceId, views),
            INTEGRATION_DEFAULT_VIEWS[scope].name
          ),
      }
    }),
    fallbackLabel: DEFAULT_VIEW_LABEL,
  }
}

/** A grid with one view (no saved views), which its address still names. */
function oneViewSource(viewName: string): RouteParamSource {
  return {
    ...fixedSource('View', gridViewOptions([], viewName)),
    fallbackLabel: DEFAULT_VIEW_LABEL,
  }
}

const byLabel = (a: RouteParamOption, b: RouteParamOption) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })

const tabOptions = (tabs: readonly { tab_key: string; tab_label: string }[]) =>
  tabs.map((tab) => ({ value: tabSlug(tab.tab_key), label: tab.tab_label }))

const ROUTE_PARAM_SOURCES: Readonly<Record<string, RouteParamSource>> = {
  ':tableId': querySource(
    'Table',
    () => ({ queryKey: tablesQueryKeys.tables, queryFn: getTables }),
    (tables) =>
      getTablesInDropdownOrder(tables).map((table) => ({
        value: table.id,
        label: table.display_name_plural ?? table.name,
      }))
  ),
  ':viewId': querySource(
    'View',
    ({ tableId }) =>
      tableId
        ? { queryKey: viewsQueryKeys.views(tableId), queryFn: () => getViews(tableId) }
        : null,
    (views) => [
      // Every table has All Records, though it isn't a saved view.
      { value: ALL_RECORDS_VIEW_ID, label: 'All Records' },
      ...views
        .filter((view) => !view.is_temporary)
        .map((view) => ({ value: view.id, label: view.name })),
    ],
    // The table's own address opens its default view, even with no views saved.
    { fallbackLabel: DEFAULT_VIEW_LABEL }
  ),
  ':fieldId': querySource(
    'Field',
    ({ tableId }) =>
      tableId
        ? { queryKey: tablesQueryKeys.tableSchema(tableId), queryFn: () => getTableSchema(tableId) }
        : null,
    (schema) =>
      schema.columns
        .map((column) => ({ value: column.id, label: column.display_name }))
        .sort(byLabel)
  ),
  ':metaTab': fixedSource(
    'Tab',
    FIELD_META_TABS.map((tab) => ({ value: tab.id, label: tab.label }))
  ),
  // Sources are kept in this browser only, never read from the server (it
  // doesn't store them yet), so the store's list is the whole list.
  ':connectionId': storeSource('Source', async () => {
    const { useIntegrationSandboxStore } =
      await import('@features/integrations/stores/useIntegrationSandboxStore')
    return {
      store: useIntegrationSandboxStore,
      selectList: (state) => state.connections,
      toOptions: (connections) =>
        connections.map((connection) => ({ value: connection.id, label: connection.name })),
    }
  }),
  '/integrations/tables/:tableId': storeSource('Table', async () => {
    const [
      { useIntegrationSandboxStore },
      { hasIntegrationsServerData, loadIntegrationsServerData },
    ] = await Promise.all([
      import('@features/integrations/stores/useIntegrationSandboxStore'),
      import('@features/integrations/hooks/useIntegrationsServerData'),
    ])
    return {
      store: useIntegrationSandboxStore,
      selectList: (state) => state.tables,
      toOptions: (tables) => tables.map((table) => ({ value: table.id, label: table.sourceName })),
      // The server's tables load with the Integrations pages (IntegrationsServerData);
      // until then the store holds the sample, which says nothing of them, so
      // the picker reads them itself.
      hasServerData: () => hasIntegrationsServerData(),
      loadServerData: loadIntegrationsServerData,
    }
  }),
  ':automationId': querySource(
    'Automation',
    () => ({ queryKey: automationsQueryKeys.list, queryFn: listAutomations }),
    (automations) =>
      automations.map((automation) => ({
        value: automation.automation_id,
        label: automation.name,
      }))
  ),
  ':userGroupId': querySource(
    'Group',
    () => ({ queryKey: permissionsQueryKeys.permissionsUserGroups, queryFn: getAllUserGroups }),
    (groups) =>
      groups.map((group) => ({ value: group.user_group_id, label: group.user_group_name }))
  ),
  ':preferenceGroupId': querySource(
    'Group',
    () => ({ queryKey: settingsQueryKeys.preferenceGroups, queryFn: () => getPreferenceGroups() }),
    (groups) =>
      groups.map((group) => ({
        value: group.preference_group_id,
        label: group.preference_group_label,
      }))
  ),
  '/me/preferences/:preferenceTabId': querySource(
    'Tab',
    () => ({ queryKey: settingsQueryKeys.userPreferences, queryFn: getUserPreferences }),
    (preferences) => tabOptions(preferences.tabs)
  ),
  '/users/preference-defaults/:preferenceTabId': querySource(
    'Tab',
    () => ({ queryKey: settingsQueryKeys.preferenceDefaults, queryFn: getPreferenceDefaults }),
    (defaults) => tabOptions(defaults.tabs)
  ),
  '/users/preference-groups/:preferenceGroupId/tabs/:preferenceTabId': querySource(
    'Tab',
    ({ preferenceGroupId }) =>
      preferenceGroupId
        ? {
            queryKey: settingsQueryKeys.preferenceGroup(preferenceGroupId),
            queryFn: () => getPreferenceGroup(preferenceGroupId),
          }
        : null,
    (group) =>
      group.tabs
        .filter((tab) => tab.tab_is_active)
        .map((tab) => ({ value: tabSlug(tab.tab_key), label: tab.tab_label }))
  ),
  // Grid pages' views, by their full address: each grid has its own views.
  '/automations/automations/views/:viewId': automationViewSource('automations'),
  '/automations/runs/views/:viewId': automationViewSource('runs'),
  '/automations/automations/:automationId/runs/views/:viewId':
    automationViewSource('automation-runs'),
  '/integrations/sources/views/:viewId': integrationViewSource('connections'),
  '/integrations/sources/:connectionId/tables/views/:viewId':
    integrationViewSource('source-tables'),
  '/integrations/sources/:connectionId/fields/views/:viewId':
    integrationViewSource('source-fields'),
  '/integrations/sources/:connectionId/runs/views/:viewId':
    integrationViewSource('connection-runs'),
  '/integrations/tables/views/:viewId': integrationViewSource('all-source-tables'),
  '/integrations/tables/:tableId/fields/views/:viewId': integrationViewSource('table-fields'),
  '/integrations/fields/views/:viewId': integrationViewSource('all-source-fields'),
  '/integrations/runs/views/:viewId': integrationViewSource('runs'),
  '/users/users/views/:viewId': oneViewSource('All users'),
  '/users/permission-groups/views/:viewId': oneViewSource('All permission groups'),
  '/users/preference-groups/views/:viewId': oneViewSource('All preference groups'),
  // Credentials has its own route, and Preferences isn't a Settings page.
  ':settingsPageKey': querySource(
    'Page',
    () => ({ queryKey: settingsQueryKeys.settings, queryFn: getSettings }),
    (settings) =>
      settings.pages
        .filter(
          (page) =>
            page.is_active && page.page_key !== 'credentials' && page.page_key !== 'preferences'
        )
        .map((page) => ({ value: page.page_key, label: page.page_label }))
  ),
  // A page's first tab is the page's own address, so only the rest are listed.
  ':settingsTabKey': querySource(
    'Tab',
    () => ({ queryKey: settingsQueryKeys.settings, queryFn: getSettings }),
    (settings, { settingsPageKey }) =>
      (settings.pages.find((page) => page.page_key === settingsPageKey)?.tabs ?? [])
        .slice(1)
        .map((tab) => ({ value: encodeURIComponent(tab.tab_key), label: tab.tab_label }))
  ),
}

/** The source for a parameter, by its full pattern first, then by its name. */
export function findRouteParamSource(paramPattern: string): RouteParamSource | undefined {
  const segment = paramPattern.slice(paramPattern.lastIndexOf('/') + 1)
  return ROUTE_PARAM_SOURCES[paramPattern] ?? ROUTE_PARAM_SOURCES[segment]
}
